import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AuthResponse } from '../types';
import { authApi } from '../api/auth';

interface AuthContextType {
  user: AuthResponse | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (username: string, oldPassword: string, newPassword: string) => Promise<void>;
  setUser: (next: AuthResponse) => void;
  /** Pobiera świeży stan konta z backendu (np. po zmianie logo firmy) i aktualizuje kontekst. */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = 'mm_user';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthResponse | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  });

  const login = useCallback(async (username: string, password: string) => {
    const { data } = await authApi.login({ username, password });
    setUser(data);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (e) {
      console.warn('Logout request failed, clearing local state anyway', e);
    }
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const resetPassword = useCallback(async (username: string, oldPassword: string, newPassword: string) => {
    await authApi.resetPassword({ username, oldPassword, newPassword });
  }, []);

  const replaceUser = useCallback((next: AuthResponse) => {
    setUser(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await authApi.me();
    setUser(data);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, resetPassword, setUser: replaceUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
